type ColorChannels = [number, number, number];

interface OklchColor {
    c: number
    h: number
    l: number
}

const HEX_COLOR_PATTERN = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const GAMUT_SEARCH_ITERATIONS = 12;

const clamp = (value:number, minimum = 0, maximum = 1):number => (
    Math.min(Math.max(value, minimum), maximum)
);

const parseHexColor = (color:string):ColorChannels|undefined => {
    const match = HEX_COLOR_PATTERN.exec(color);
    if(!match) return undefined;

    return [
        Number.parseInt(match[1], 16) / 255,
        Number.parseInt(match[2], 16) / 255,
        Number.parseInt(match[3], 16) / 255,
    ];
};

const srgbToLinear = (channel:number):number => (
    channel <= 0.04045
        ? channel / 12.92
        : ((channel + 0.055) / 1.055) ** 2.4
);

const linearToSrgb = (channel:number):number => (
    channel <= 0.0031308
        ? channel * 12.92
        : 1.055 * channel ** (1 / 2.4) - 0.055
);

const srgbToOklch = ([red, green, blue]:ColorChannels):OklchColor => {
    const linearRed = srgbToLinear(red);
    const linearGreen = srgbToLinear(green);
    const linearBlue = srgbToLinear(blue);
    const lRoot = Math.cbrt(
        0.4122214708 * linearRed
        + 0.5363325363 * linearGreen
        + 0.0514459929 * linearBlue
    );
    const mRoot = Math.cbrt(
        0.2119034982 * linearRed
        + 0.6806995451 * linearGreen
        + 0.1073969566 * linearBlue
    );
    const sRoot = Math.cbrt(
        0.0883024619 * linearRed
        + 0.2817188376 * linearGreen
        + 0.6299787005 * linearBlue
    );
    const l = (
        0.2104542553 * lRoot
        + 0.793617785 * mRoot
        - 0.0040720468 * sRoot
    );
    const a = (
        1.9779984951 * lRoot
        - 2.428592205 * mRoot
        + 0.4505937099 * sRoot
    );
    const b = (
        0.0259040371 * lRoot
        + 0.7827717662 * mRoot
        - 0.808675766 * sRoot
    );

    return {
        c: Math.hypot(a, b),
        h: Math.atan2(b, a),
        l,
    };
};

const oklchToLinearSrgb = ({c, h, l}:OklchColor):ColorChannels => {
    const a = c * Math.cos(h);
    const b = c * Math.sin(h);
    const lRoot = l + 0.3963377774 * a + 0.2158037573 * b;
    const mRoot = l - 0.1055613458 * a - 0.0638541728 * b;
    const sRoot = l - 0.0894841775 * a - 1.291485548 * b;
    const linearL = lRoot ** 3;
    const linearM = mRoot ** 3;
    const linearS = sRoot ** 3;

    return [
        (
            4.0767416621 * linearL
            - 3.3077115913 * linearM
            + 0.2309699292 * linearS
        ),
        (
            -1.2684380046 * linearL
            + 2.6097574011 * linearM
            - 0.3413193965 * linearS
        ),
        (
            -0.0041960863 * linearL
            - 0.7034186147 * linearM
            + 1.707614701 * linearS
        ),
    ];
};

const isInSrgbGamut = (channels:ColorChannels):boolean => (
    channels.every(channel => channel >= 0 && channel <= 1)
);

/**
 * Keeps OKLCH lightness and hue intact while reducing chroma only when the
 * requested color falls outside the displayable sRGB gamut.
 */
const mapOklchToSrgbGamut = (color:OklchColor):ColorChannels => {
    let mappedColor = color;
    let channels = oklchToLinearSrgb(mappedColor);
    if(isInSrgbGamut(channels)) {
        return channels.map(linearToSrgb) as ColorChannels;
    }

    let minimumChroma = 0;
    let maximumChroma = color.c;
    for(
        let iteration = 0;
        iteration < GAMUT_SEARCH_ITERATIONS;
        iteration += 1
    ) {
        const chroma = (minimumChroma + maximumChroma) / 2;
        const candidate = {...color, c: chroma};
        const candidateChannels = oklchToLinearSrgb(candidate);

        if(isInSrgbGamut(candidateChannels)) {
            minimumChroma = chroma;
            mappedColor = candidate;
            channels = candidateChannels;
        } else {
            maximumChroma = chroma;
        }
    }

    return channels.map(channel => linearToSrgb(clamp(channel))) as ColorChannels;
};

const formatHexColor = (channels:ColorChannels):string => (
    `#${channels
        .map(channel => Math.round(clamp(channel) * 255)
            .toString(16)
            .padStart(2, '0'))
        .join('')}`
);

/**
 * Separates a solid pedestal from its cyclorama while keeping the pair in the
 * same color family. Light backgrounds produce a darker pedestal and dark
 * backgrounds produce a lighter one, with reduced chroma in either direction.
 */
export const deriveStudioPedestalColor = (backgroundColor:string):string => {
    const backgroundChannels = parseHexColor(backgroundColor);
    if(!backgroundChannels) return backgroundColor;

    const background = srgbToOklch(backgroundChannels);
    const pedestal:OklchColor = {
        c: background.c * 0.65,
        h: background.h,
        l: clamp(background.l + (background.l < 0.65 ? 0.15 : -0.15)),
    };

    return formatHexColor(mapOklchToSrgbGamut(pedestal));
};
